/**
 * Shared type for runtime tool definitions used by the agentic chat component.
 */
export interface RuntimeToolDef {
  definition: {
    name: string;
    description: string;
    deferred: boolean;
    title: string;
    parameters: {
      type: "object";
      properties: Record<string, any>;
      required?: string[];
    };
  };
  callback: (args: any) => Promise<string>;
  timeOutInMs: number;
}

const WORKSPACE_TOOL_PREFIX = "WS-";
const API_NAMESPACE_SUFFIX = "Api";

export function removeWorkspaceToolPrefix(name: string): string {
  return name.startsWith(WORKSPACE_TOOL_PREFIX)
    ? name.slice(WORKSPACE_TOOL_PREFIX.length)
    : name;
}

export function toModelToolNamespace(namespace: string): string {
  return namespace.endsWith(API_NAMESPACE_SUFFIX)
    ? namespace.slice(0, -API_NAMESPACE_SUFFIX.length)
    : namespace;
}

export function normalizeWorkspaceToolName(name: string): string {
  if (!name.startsWith(WORKSPACE_TOOL_PREFIX)) return name;

  const withoutPrefix = removeWorkspaceToolPrefix(name);
  const separatorIndex = withoutPrefix.indexOf("-");
  const namespace =
    separatorIndex === -1
      ? withoutPrefix
      : withoutPrefix.slice(0, separatorIndex);
  const remainder =
    separatorIndex === -1 ? "" : withoutPrefix.slice(separatorIndex);

  return `${toModelToolNamespace(namespace)}${remainder}`;
}

export function toToolDisplayName(name: string): string {
  const normalizedName = normalizeWorkspaceToolName(name);
  const operationName = normalizedName.split(/__|-/).at(-1) ?? normalizedName;

  return operationName
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function normalizeRuntimeTool(tool: RuntimeToolDef): RuntimeToolDef {
  return {
    ...tool,
    definition: {
      ...tool.definition,
      name: normalizeWorkspaceToolName(tool.definition.name),
      title: toToolDisplayName(tool.definition.name),
      description: tool.definition.description.replace(
        /WS-([A-Za-z0-9]+)(?=-|\b)/g,
        (_, namespace: string) => toModelToolNamespace(namespace),
      ),
    },
  };
}

export function normalizeRuntimeTools(
  tools: Record<string, RuntimeToolDef>,
): Record<string, RuntimeToolDef> {
  const normalizedTools: Record<string, RuntimeToolDef> = {};

  for (const tool of Object.values(tools)) {
    const normalizedTool = normalizeRuntimeTool(tool);
    const name = normalizedTool.definition.name;
    if (normalizedTools[name]) {
      throw new Error(`Duplicate model-facing tool name: ${name}`);
    }
    normalizedTools[name] = normalizedTool;
  }

  return normalizedTools;
}
